import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "components/button";
import Icon from "components/icon";
import ConfirmationModal from "components/shared/confirmation-modal";
import useEditDriver from "./useEditDriver";
import DriverProfileTab from "./components/driver-profile-tab";
import DriverEmergencyTab from "./components/driver-emergency-tab";
import DriverFinancialTab from "./components/driver-financial-tab";
import { editDriverPageRouteName, driverListPageRouteName } from "../constants/routes";

export { editDriverPageRouteName };

const EditDriverPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);

  const {
    t,
    form,
    vendorOptions,
    isLoadingVendorOptions,
    isUpdating,
    isUpdatingDraft,
    isLoadingDetail,
    onSearchVendors,
    driverPictureUrl,
    driverPictureName,
    isUploadingDriverPicture,
    documentFiles,
    documentErrors,
    validateDocuments,
    expiryDates,
    handleExpiryDateChange,
    handleDriverPictureChange,
    handleDocumentFileChange,
    handleConfirmSave,
    handleSaveDraft,
    isDraft,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
  } = useEditDriver(id!);

  if (isLoadingDetail) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Icon name="loading" size={40} className="animate-spin text-primary-500" />
      </div>
    );
  }

  const tabs = [
    { id: "profile", label: t("tab-driver-profile"), icon: "profile-2user" as const },
    { id: "emergency", label: t("tab-emergency-contacts"), icon: "phone" as const },
    { id: "financial", label: t("tab-financial-documents"), icon: "document-text" as const },
  ];

  return (
    <div className="flex flex-col min-h-[calc(100vh-112px)] -mb-3">
      {/* Tabs Header */}
      <div className="flex border-b border-border bg-white -mx-3 px-3">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(index)}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold transition-all border-b-2 -mb-[2px] ${
              activeTab === index
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon name={tab.icon} size={20} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 py-4">
        <section className="rounded-2xl bg-whiteScale-90 px-6 py-6 h-full overflow-visible">
          {activeTab === 0 && (
            <DriverProfileTab
              form={form}
              t={t}
              vendorOptions={vendorOptions}
              isLoadingVendorOptions={isLoadingVendorOptions}
              onSearchVendors={onSearchVendors}
              driverPictureUrl={driverPictureUrl}
              driverPictureName={driverPictureName}
              isUploadingDriverPicture={isUploadingDriverPicture}
              onDriverPictureChange={handleDriverPictureChange}
            />
          )}
          {activeTab === 1 && (
            <DriverEmergencyTab
              form={form}
              t={t}
            />
          )}
          {activeTab === 2 && (
            <DriverFinancialTab
              form={form}
              t={t}
              documentFiles={documentFiles}
              documentErrors={documentErrors}
              onDocumentFileChange={handleDocumentFileChange}
              expiryDates={expiryDates}
              onExpiryDateChange={handleExpiryDateChange}
            />
          )}
        </section>
      </div>

      {/* Bottom Action Bar */}
      <div className="mt-auto -mx-3 bg-white border-t border-border px-6 py-3 flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          onClick={() => setShowCancelModal(true)}
        >
          {t("btn-cancel")}
        </Button>
        {isDraft && (
          <Button
            type="button"
            variant="outline"
            color="secondary"
            onClick={handleSaveDraft}
            disabled={isUpdating || isUpdatingDraft}
          >
            {isUpdatingDraft ? <Icon name="loading" size={18} className="animate-spin" /> : t("btn-save-draft")}
          </Button>
        )}
        <Button
          type="button"
          variant="fill"
          color="primary"
          onClick={async () => {
            const isValid = await form.trigger();
            if (!isValid) {
              toast.error(t("toast-complete-required-fields"));
              return;
            }
            const isDocsValid = validateDocuments();
            if (!isDocsValid) {
              return;
            }
            setShowSaveModal(true);
          }}
          disabled={isUpdating || isUpdatingDraft}
        >
          {isUpdating ? <Icon name="loading" size={18} className="animate-spin" /> : t("btn-save")}
        </Button>
      </div>

      {/* Confirmation Modals */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-description")}
        confirmLabel={t("btn-confirm")}
        cancelLabel={t("btn-cancel")}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowSaveModal(false)}
        loading={isUpdating}
        icon="info"
      />

      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-cancel-title")}
        message={t("modal-cancel-description")}
        confirmLabel={t("btn-confirm")}
        cancelLabel={t("btn-cancel")}
        onConfirm={() => {
          if (location.state?.from === "draft") {
            navigate("/master-data/driver/draft");
          } else if (location.state?.from === "detail") {
            navigate(`/master-data/driver/${id}/detail`);
          } else {
            navigate(driverListPageRouteName);
          }
        }}
        onCancel={() => setShowCancelModal(false)}
        icon="danger"
      />
    </div>
  );
};

export default EditDriverPage;
